import { type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useIntl } from 'react-intl';

import Spacings from '@dynatrace/strato-design-tokens/spacings';
import { ZoomInIcon, ZoomOutIcon } from '@dynatrace/strato-icons';

import { useGridPlotSize } from '../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { ChartToolbarRenderer } from '../../../core/components/toolbar/ChartToolbarRenderer.js';
import { useTriggerZoom } from '../../../core/components/zoom/hooks/useTriggerZoom.js';
import { useA11yToolbarTabIndex } from '../../../core/hooks/useA11yToolbarTabIndex.js';
import { ZOOM_ACTIONS, ZOOM_CHANGE_TYPE } from '../../../core/types/zoom.js';
import { getKeyShortcutByAgent } from '../../../core/utils/get-key-shortcut-by-agent.js';
import { useZoomState } from '../../hooks/store/selectors.js';
import { HISTOGRAM_MESSAGES } from '../../messages/tooltip-text.js';
import { useApplyZoomDelta } from '../overlays/hooks/useApplyZoomDelta.js';

export const ZoomInOutButtons = () => {
  const tabIndexIn = useA11yToolbarTabIndex(ZOOM_ACTIONS.ZoomIn);
  const tabIndexOut = useA11yToolbarTabIndex(ZOOM_ACTIONS.ZoomOut);
  const { zoomInEnabled, zoomOutEnabled } = useZoomState();
  const intl = useIntl();

  const { width } = useGridPlotSize();
  const applyZoom = useApplyZoomDelta();
  const triggerZoom = useTriggerZoom(width, applyZoom);

  const handleZoomInClick = () => {
    triggerZoom(ZOOM_CHANGE_TYPE.ZoomIn);
  };

  const handleZoomInKeyDown = (event: ReactKeyboardEvent) => {
    if (event.code === 'Enter') {
      triggerZoom(ZOOM_CHANGE_TYPE.ZoomIn);
    }
  };

  const handleZoomOutClick = () => {
    triggerZoom(ZOOM_CHANGE_TYPE.ZoomOut);
  };

  const handleZoomOutKeyDown = (event: ReactKeyboardEvent) => {
    if (event.code === 'Enter') {
      triggerZoom(ZOOM_CHANGE_TYPE.ZoomOut);
    }
  };

  const zoomInShortcut =
    getKeyShortcutByAgent('CONTROL') +
    `+${intl.formatMessage(HISTOGRAM_MESSAGES.zoomActionArrowUp)}`;
  const zoomOutShortcut =
    getKeyShortcutByAgent('CONTROL') +
    `+${intl.formatMessage(HISTOGRAM_MESSAGES.zoomActionArrowDown)}`;

  const controlStyle = { marginRight: Spacings.Size2 };

  return (
    <>
      <ChartToolbarRenderer.Control
        prefixIcon={<ZoomInIcon />}
        tooltipText={intl.formatMessage(HISTOGRAM_MESSAGES.zoomIn)}
        tooltipKeyShortcut={zoomInShortcut}
        onKeyDown={handleZoomInKeyDown}
        onClick={handleZoomInClick}
        tabIndex={tabIndexIn}
        disabled={!zoomInEnabled}
        divider={'left'}
        style={controlStyle}
      />
      <ChartToolbarRenderer.Control
        prefixIcon={<ZoomOutIcon />}
        tooltipText={intl.formatMessage(HISTOGRAM_MESSAGES.zoomOut)}
        tooltipKeyShortcut={zoomOutShortcut}
        onKeyDown={handleZoomOutKeyDown}
        onClick={handleZoomOutClick}
        tabIndex={tabIndexOut}
        disabled={!zoomOutEnabled}
        style={controlStyle}
      />
    </>
  );
};

ZoomInOutButtons['displayName'] = 'ZoomInOutButtons';
